import { useState } from "react";
import { Coffee, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Buy-me-a-coffee tip card. Reusable — used both in blog/page sections
 * (via SectionRenderer's `tip_jar` subtype) and in the site footer.
 *
 * Design notes matching the reference:
 *   - Rounded pastel card, 1px accent border, subtle inner icon tile
 *   - Copy on the left, pill-shaped CTA on the right, wraps below on mobile
 *   - Amount presets (3 by default) + optional custom amount input
 *
 * Props:
 *   heading, body, buttonLabel — display strings (all pre-sanitized upstream)
 *   amountsCents: number[]    — up to 5 preset amounts in cents
 *   allowCustom: boolean      — show a custom-amount input
 *   variant: "card" | "compact" — "compact" tightens paddings for the footer
 */
export default function TipJarCard({
  heading = "Enjoying Hospice Synopsis?",
  body = "This site is free, ad-light, and built by one person. If it's helped you, you can say thanks with a coffee.",
  buttonLabel = "Buy me a coffee",
  amountsCents = [300, 500, 1000],
  allowCustom = true,
  variant = "card",
  className = "",
}) {
  const [selected, setSelected] = useState(amountsCents[0] || 500);
  const [customValue, setCustomValue] = useState(""); // dollars, string for controlled input
  const [busy, setBusy] = useState(false);

  const dollars = (c) => (c / 100).toLocaleString(undefined, {
    minimumFractionDigits: c % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });

  const chosenCents = () => {
    if (customValue.trim() !== "") {
      const d = Number.parseFloat(customValue);
      if (!Number.isFinite(d)) return null;
      const c = Math.round(d * 100);
      if (c < 100 || c > 50000) return null;
      return c;
    }
    return selected;
  };

  const startCheckout = async () => {
    const cents = chosenCents();
    if (!cents) {
      toast.error("Enter an amount between $1 and $500.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/tips/create-checkout-session", {
        amount_cents: cents,
        currency: "usd",
        origin_url: window.location.origin,
      });
      window.location.assign(r.data.checkout_url);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't start checkout. Please try again.");
      setBusy(false);
    }
  };

  const isCompact = variant === "compact";
  const padding = isCompact ? "p-4 sm:p-5" : "p-5 sm:p-6";
  const iconTileSize = isCompact ? "w-11 h-11" : "w-14 h-14";

  return (
    <div
      data-testid="tip-jar-card"
      className={`${padding} rounded-2xl border border-amber-200 bg-amber-50/70 dark:bg-amber-950/20 dark:border-amber-900/60 ${className}`}
    >
      <div className="flex items-start gap-4 sm:gap-5">
        {/* Icon tile */}
        <div className={`${iconTileSize} shrink-0 rounded-xl bg-white dark:bg-slate-900 border border-amber-100 dark:border-amber-900/60 grid place-items-center shadow-sm`}>
          <Coffee className="w-5 h-5 text-amber-800 dark:text-amber-300" aria-hidden />
        </div>

        {/* Copy */}
        <div className="min-w-0 flex-1">
          <div className="font-heading font-semibold text-slate-900 dark:text-slate-100 text-base sm:text-lg leading-snug"
               data-testid="tip-jar-heading">
            {heading}
          </div>
          <p className="mt-1 text-sm text-slate-700 dark:text-slate-300 leading-relaxed"
             data-testid="tip-jar-body">
            {body}
          </p>

          {/* Amount picker */}
          <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="tip-jar-amounts">
            {amountsCents.map(c => (
              <button
                key={c}
                type="button"
                data-testid={`tip-jar-amount-${c}`}
                onClick={() => { setSelected(c); setCustomValue(""); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  (customValue.trim() === "" && selected === c)
                    ? "bg-amber-400 border-amber-500 text-slate-900 shadow-sm"
                    : "bg-white/70 dark:bg-slate-900/40 border-amber-200 dark:border-amber-900/60 text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-900"
                }`}
              >
                ${dollars(c)}
              </button>
            ))}
            {allowCustom && (
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                <input
                  data-testid="tip-jar-custom-amount"
                  type="number"
                  min="1"
                  max="500"
                  step="1"
                  inputMode="decimal"
                  placeholder="Other"
                  value={customValue}
                  onChange={e => setCustomValue(e.target.value)}
                  className="w-24 pl-5 pr-2 py-1.5 rounded-full text-sm border border-amber-200 dark:border-amber-900/60 bg-white/70 dark:bg-slate-900/40 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-400"
                />
              </div>
            )}
          </div>
        </div>

        {/* CTA — full-width on mobile, inline on ≥sm */}
        <div className="hidden sm:block shrink-0 self-center">
          <TipButton busy={busy} onClick={startCheckout} label={buttonLabel} />
        </div>
      </div>

      {/* Mobile CTA */}
      <div className="mt-4 sm:hidden">
        <TipButton busy={busy} onClick={startCheckout} label={buttonLabel} full />
      </div>
    </div>
  );
}

function TipButton({ busy, onClick, label, full = false }) {
  return (
    <button
      data-testid="tip-jar-cta"
      onClick={onClick}
      disabled={busy}
      className={`inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold px-5 py-2.5 shadow-sm hover:shadow transition-colors disabled:opacity-60 ${full ? "w-full" : ""}`}
    >
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
      ) : (
        <Coffee className="w-4 h-4" aria-hidden />
      )}
      <span>{busy ? "Redirecting…" : label}</span>
    </button>
  );
}
